import * as THREE from "three";
import {
  makePerspectiveCamera,
  updatePerspectiveCamera,
} from "./TransformUtils";
import { createImageLayer } from "./ImageLayer";
import {
  createClockLayers,
  updateClockLayers,
  updateClockLayerTransforms,
} from "./ClockLayer";

const CANVAS_W = 1080;
const CANVAS_H = 2400;

const CLOCK_MATRIX_SETTINGS = new Set([
  "clockHPos",
  "clockVPos",
  "clockRotation",
  "perspRotX",
  "perspRotY",
  "perspRotZ",
  "hStretch",
  "vStretch",
  "hSkew",
  "vSkew",
]);

export function getClockSettingsUpdateType(previous, next) {
  if (!previous || !next) return "texture";

  let matrixChanged = false;
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  for (const key of keys) {
    const prevVal = previous[key];
    const nextVal = next[key];
    if (Object.is(prevVal, nextVal)) continue;
    if (
      Array.isArray(prevVal) &&
      Array.isArray(nextVal) &&
      prevVal.length === nextVal.length &&
      prevVal.every((v, i) => v === nextVal[i])
    ) {
      continue;
    }
    if (!CLOCK_MATRIX_SETTINGS.has(key)) return "texture";
    matrixChanged = true;
  }

  return matrixChanged ? "matrix" : "none";
}

export default class WallpaperRenderer {
  constructor(canvasW = CANVAS_W, canvasH = CANVAS_H) {
    this._canvasW = canvasW;
    this._canvasH = canvasH;
    this._renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true,
    });
    this._renderer.setSize(canvasW, canvasH);
    this._renderer.setPixelRatio(1);
    this._renderer.setClearColor(0x000000, 1);
    this._renderer.sortObjects = true;
    this._renderer.outputColorSpace = THREE.SRGBColorSpace;
    this._renderer.toneMapping = THREE.NoToneMapping;

    this._scene = new THREE.Scene();
    this._camera = makePerspectiveCamera(canvasW, canvasH);

    this._bgLayer    = null;
    this._fgLayer    = null;
    this._clockLayers = null;

    this._bgSrc  = null;
    this._fgSrc  = null;
    this._cs     = null;

    this._updating = false;
    this._pendingUpdate = false;
  }

  // ─── Public accessors ──────────────────────────────────────────────────────

  get domElement() {
    return this._renderer.domElement;
  }

  // ─── Layer management ─────────────────────────────────────────────────────

  _addMesh(mesh) {
    if (mesh) this._scene.add(mesh);
  }

  _removeMeshes(...meshes) {
    meshes.forEach((m) => { if (m) this._scene.remove(m); });
  }

  async _rebuildBg(src) {
    if (this._bgLayer) {
      this._removeMeshes(this._bgLayer.mesh);
      this._bgLayer.dispose();
      this._bgLayer = null;
    }
    if (src) {
      this._bgLayer = await createImageLayer(
        src,
        this._canvasW,
        this._canvasH,
        0,
        false
      );
      this._addMesh(this._bgLayer.mesh);
    }
  }

  async _rebuildFg(src) {
    if (this._fgLayer) {
      this._removeMeshes(this._fgLayer.mesh);
      this._fgLayer.dispose();
      this._fgLayer = null;
    }
    if (src) {
      this._fgLayer = await createImageLayer(
        src,
        this._canvasW,
        this._canvasH,
        30,
        true
      );
      this._addMesh(this._fgLayer.mesh);
    }
  }

  async _rebuildClock(cs) {
    if (this._clockLayers) {
      this._removeMeshes(
        this._clockLayers.backMesh,
        this._clockLayers.frontMesh,
        this._clockLayers.dateMesh
      );
      this._clockLayers.dispose();
      this._clockLayers = null;
    }
    if (cs) {
      this._clockLayers = await createClockLayers(
        cs,
        this._canvasW,
        this._canvasH
      );
      this._addMesh(this._clockLayers.backMesh);
      this._addMesh(this._clockLayers.dateMesh);
      this._addMesh(this._clockLayers.frontMesh);
    }
  }

  // ─── Main update ──────────────────────────────────────────────────────────

  /**
   * Full update: rebuilds layers that changed.
   * @param {string|null} bgSrc
   * @param {string|null} fgSrc
   * @param {object} clockSettings
   */
  async update(bgSrc, fgSrc, clockSettings, forceClockPaint = false) {
    if (this._updating) {
      const pendingForceClockPaint = this._pendingArgs?.[3] || forceClockPaint;
      this._pendingArgs = [
        bgSrc,
        fgSrc,
        clockSettings,
        pendingForceClockPaint,
      ];
      this._pendingUpdate = true;
      return;
    }
    this._updating = true;
    try {
      const bgChanged = bgSrc !== this._bgSrc;
      const fgChanged = fgSrc !== this._fgSrc;
      const nextClockSettings = clockSettings
        ? { ...clockSettings }
        : clockSettings;
      const clockUpdateType = forceClockPaint
        ? "texture"
        : getClockSettingsUpdateType(this._cs, nextClockSettings);

      const tasks = [];
      if (bgChanged) {
        this._bgSrc = bgSrc;
        tasks.push(this._rebuildBg(bgSrc));
      }
      if (fgChanged) {
        this._fgSrc = fgSrc;
        tasks.push(this._rebuildFg(fgSrc));
      }

      this._cs = nextClockSettings;
      if (this._clockLayers) {
        if (clockUpdateType === "texture") {
          tasks.push(
            updateClockLayers(
              this._clockLayers,
              nextClockSettings,
              this._canvasW,
              this._canvasH
            )
          );
        } else if (clockUpdateType === "matrix") {
          updateClockLayerTransforms(
            this._clockLayers,
            nextClockSettings,
            this._canvasW,
            this._canvasH
          );
        }
      } else {
        tasks.push(this._rebuildClock(nextClockSettings));
      }

      await Promise.all(tasks);
      this._render();
    } finally {
      this._updating = false;
      if (this._pendingUpdate) {
        this._pendingUpdate = false;
        const args = this._pendingArgs;
        this._pendingArgs = null;
        this.update(...args);
      }
    }
  }

  _render() {
    updatePerspectiveCamera(
      this._camera,
      this._cs,
      this._canvasW,
      this._canvasH
    );
    this._renderer.render(this._scene, this._camera);
  }

  /**
   * Force a fresh clock repaint + render — call every minute to advance time.
   */
  async tick() {
    if (!this._cs) return;
    return this.update(this._bgSrc, this._fgSrc, this._cs, true);
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  /**
   * Returns the current frame as a Blob (PNG).
   * This is the exact same canvas that is displayed in the UI.
   */
  exportBlob() {
    return new Promise((resolve, reject) => {
      this._render();
      this._renderer.domElement.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("toBlob returned null"))),
        "image/png"
      );
    });
  }

  // ─── Cleanup ───────────────────────────────────────────────────────────────

  dispose() {
    if (this._bgLayer)    { this._bgLayer.dispose();    }
    if (this._fgLayer)    { this._fgLayer.dispose();    }
    if (this._clockLayers){ this._clockLayers.dispose(); }
    this._scene.clear();
    this._renderer.dispose();
  }
}
